
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`Extending Styles`}</h2>
    <p>{`Quite frequently you might want to use a component, but change it slightly for a single case. Now, you could pass in an interpolated function and change them based on some props, but that's quite a lot of effort for overriding the styles once.`}</p>
    <p>{`To easily make a new component that inherits the styling of another, just wrap it in the `}<inlineCode parentName="p">{`styled()`}</inlineCode>{` constructor. Here we use the button from the last section and create a special one, extending it with some color-related styling:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`// The Button from the last section without the interpolations
const Button = styled.button\`
  color: palevioletred;
  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border: 2px solid palevioletred;
  border-radius: 3px;
\`;

// A new component based on Button, but with some override styles
const TomatoButton = styled(Button)\`
  color: tomato;
  border-color: tomato;
\`;

render(
  <div>
    <Button>Normal Button</Button>
    <TomatoButton>Tomato Button</TomatoButton>
  </div>
);
`}</code></pre>
    <p>{`We can see that the new `}<inlineCode parentName="p">{`TomatoButton`}</inlineCode>{` still resembles `}<inlineCode parentName="p">{`Button`}</inlineCode>{`, while we have only added two new rules.`}</p>
    <p>{`In some cases you might want to change which tag or component a styled component renders. This is common when building a navigation bar for example, where there are a mix of anchor links and buttons but they should be styled identically.`}</p>
    <p>{`For this situation, we have an escape hatch. You can use the `}<a parentName="p" {...{
        "href": "/docs/api#as-polymorphic-prop"
      }}><inlineCode parentName="a">{`"as" polymorphic prop`}</inlineCode></a>{` to dynamically swap out the element that receives the styles you wrote:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`const Button = styled.button\`
  display: inline-block;
  color: palevioletred;
  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border: 2px solid palevioletred;
  border-radius: 3px;
  display: block;
\`;

const TomatoButton = styled(Button)\`
  color: tomato;
  border-color: tomato;
\`;

render(
  <div>
    <Button>Normal Button</Button>
    <Button as="a" href="#">Link with Button styles</Button>
    <TomatoButton as="a" href="#">Link with Tomato Button styles</TomatoButton>
  </div>
);
`}</code></pre>
    <p>{`This works perfectly fine with custom components too!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react"
      }}>{`const Button = styled.button\`
  display: inline-block;
  color: palevioletred;
  font-size: 1em;
  margin: 1em;
  padding: 0.25em 1em;
  border: 2px solid palevioletred;
  border-radius: 3px;
  display: block;
\`;

const ReversedButton = props => <Button {...props} children={props.children.split('').reverse()} />

render(
  <div>
    <Button>Normal Button</Button>
    <Button as={ReversedButton}>Custom Button with Normal Button styles</Button>
  </div>
);
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`If you are still on an older version than v4, you can use the `}<a parentName="p" {...{
          "href": "/docs/api#withcomponent"
        }}><inlineCode parentName="a">{`.withComponent`}</inlineCode></a>{` or `}<a parentName="p" {...{
          "href": "/docs/api#deprecated-extend"
        }}><inlineCode parentName="a">{`.extend`}</inlineCode></a>{` API's to achieve the same result as with the `}<a parentName="p" {...{
          "href": "/docs/api#as-polymorphic-prop"
        }}><inlineCode parentName="a">{`"as" prop`}</inlineCode></a>{`, but note that this is discouraged as with v4 `}<a parentName="p" {...{
          "href": "/releases#breaking-changes"
        }}><inlineCode parentName="a">{`.extend`}</inlineCode>{` was removed`}</a>{` and `}<inlineCode parentName="p">{`.withComponent`}</inlineCode>{` was marked as a candidate for future deprecation.`}</p>
    </blockquote>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;